import React, { useState } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';

import { ModalDefault } from '../../../../components/ModalDefault/ModalDefault';
import { ControlledInput } from '../../../../components/ControlledInput/ControlledInput';
import { Button } from '../../../../components/Button/Button';
import { Loading } from '../../../../components/Loading/Loading';
import {
  useConfirmTgLoginMutation,
  useCreateTgSessionMutation,
  useSendTgCodeMutation,
} from '../../../../redux/services/TgSessionsApi';
import { type TgSession } from '../../../../redux/types/TgSessionsTypes';
import { RequisitesSelect } from './RequisitesSelect';
import { getErrorText } from './helpers';
import styles from '../TgSessions.module.css';

type Step = 'form' | 'code' | 'password' | 'done';

interface SessionLoginModalProps {
  active: boolean;
  setActive: (value: boolean) => void;
  session?: TgSession;
}

interface TInputs {
  phone: string;
  label: string;
  code: string;
  password: string;
}

const initialStep = (session?: TgSession): Step => {
  if (!session) return 'form';
  return session.status === 'PASSWORD_REQUIRED' ? 'password' : 'code';
};

const SessionLoginModal = ({ active, setActive, session }: SessionLoginModalProps) => {
  const formMethods = useForm<TInputs>({ mode: 'onChange', shouldUnregister: false });
  const { handleSubmit, watch } = formMethods;
  const [step, setStep] = useState<Step>(initialStep(session));
  const [sessionId, setSessionId] = useState<number | undefined>(session?.id);
  const [requisites, setRequisites] = useState<number[]>(session?.requisites || []);
  const [error, setError] = useState<string>('');
  const [createSession, { isLoading: isCreating }] = useCreateTgSessionMutation();
  const [sendCode, { isLoading: isSending }] = useSendTgCodeMutation();
  const [confirmLogin, { isLoading: isConfirming }] = useConfirmTgLoginMutation();
  const phone = watch('phone');
  const code = watch('code');
  const password = watch('password');

  const applyStatus = (updated: TgSession) => {
    if (updated.status === 'ACTIVE') setStep('done');
    else if (updated.status === 'PASSWORD_REQUIRED') setStep('password');
    else setStep('code');
  };

  const onSubmit: SubmitHandler<TInputs> = async (data) => {
    setError('');
    try {
      if (step === 'form') {
        let id = sessionId;
        if (!id) {
          const created = await createSession({ phone: data.phone, label: data.label, requisites }).unwrap();
          id = created.id;
          setSessionId(id);
        }
        const sent = await sendCode(id).unwrap();
        applyStatus(sent);
      } else if (step === 'code' && sessionId) {
        applyStatus(await confirmLogin({ id: sessionId, code: data.code }).unwrap());
      } else if (step === 'password' && sessionId) {
        applyStatus(await confirmLogin({ id: sessionId, password: data.password }).unwrap());
      }
    } catch (requestError) {
      setError(getErrorText(requestError, 'Не удалось выполнить действие, попробуйте ещё раз'));
    }
  };

  const busy = isCreating || isSending || isConfirming;
  const titles: Record<Step, string> = {
    form: 'Подключение Telegram-аккаунта',
    code: 'Введите код из Telegram',
    password: 'Введите пароль двухфакторной защиты',
    done: 'Аккаунт подключён',
  };

  return (
    <FormProvider {...formMethods}>
      <ModalDefault title={titles[step]} titleMargin={'16px'} active={active} setActive={setActive}>
        {busy ? (
          <Loading />
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            {step === 'form' && (
              <>
                <p className={styles.stepText}>
                  Укажите номер аккаунта, в который приходят уведомления банка. Telegram пришлёт код на этот номер.
                </p>
                <ControlledInput
                  title={'Номер телефона'}
                  rules={{ required: true, pattern: /^\+?[\d\s()-]{8,20}$/ }}
                  blockStyle={{ marginBottom: '16px' }}
                  placeholder={'+998 90 123 45 67'}
                  name={'phone'}
                  type={'text'}
                  required={true}
                />
                <ControlledInput
                  title={'Название (необязательно)'}
                  blockStyle={{ marginBottom: '16px' }}
                  placeholder={'Например: Uzcard основной'}
                  name={'label'}
                  type={'text'}
                />
                <RequisitesSelect value={requisites} onChange={setRequisites} />
                <Button text={'Отправить код'} disabled={!phone} type={'submit'} />
              </>
            )}
            {step === 'code' && (
              <>
                <p className={styles.stepText}>Код пришёл в Telegram на этот номер (или по SMS). Введите его.</p>
                <ControlledInput
                  title={'Код'}
                  rules={{ required: true, minLength: 4 }}
                  blockStyle={{ marginBottom: '16px' }}
                  placeholder={'12345'}
                  name={'code'}
                  type={'text'}
                  required={true}
                />
                <Button text={'Подтвердить'} disabled={!code} type={'submit'} />
              </>
            )}
            {step === 'password' && (
              <>
                <p className={styles.stepText}>У аккаунта включена двухфакторная защита — введите облачный пароль Telegram.</p>
                <ControlledInput
                  title={'Пароль'}
                  rules={{ required: true }}
                  blockStyle={{ marginBottom: '16px' }}
                  placeholder={'Пароль'}
                  name={'password'}
                  type={'password'}
                  required={true}
                />
                <Button text={'Подтвердить'} disabled={!password} type={'submit'} />
              </>
            )}
            {step === 'done' && (
              <>
                <p className={styles.stepText}>
                  Готово. Аккаунт подключён — уведомления банка будут читаться автоматически. Ничего дополнительно делать не нужно.
                </p>
                <Button text={'Закрыть'} action={() => setActive(false)} />
              </>
            )}
            {error && <div className={styles.error}>{error}</div>}
          </form>
        )}
      </ModalDefault>
    </FormProvider>
  );
};

export default SessionLoginModal;
